import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Eye,
  Trash2,
  Download,
  FileText,
  Calendar,
  DollarSign,
  Users,
  Target,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  MoreHorizontal,
  RefreshCw,
  Settings,
  TrendingUp,
  TrendingDown,
  Shield,
  Zap,
  Building,
  Briefcase,
  Star,
  Tag,
  Globe,
  Database,
  Layers,
  Workflow,
  Code,
  Bell,
  BellRing,
  UserCheck,
  Building2,
  Phone,
  Mail,
  MapPin,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Clipboard,
  Award,
  Shield as ShieldIcon,
  CreditCard,
  Receipt,
  Globe as GlobeIcon,
  Briefcase as BriefcaseIcon,
  Zap as ZapIcon,
  Paperclip,
  MessageSquare,
  PieChart,
  Upload,
  Link,
  Tag as TagIcon,
  FileBarChart,
  FileCheck,
  XCircle,
  Archive,
  FileDown,
  List,
  FilePlus,
  Brain,
  Workflow as WorkflowIcon,
  Layers as LayersIcon,
  Database as DatabaseIcon,
  Code as CodeIcon,
  Bell as BellIcon,
  BellRing as BellRingIcon,
  HelpCircle,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';
import ContractService from '../../services/contractService';
import MasterDataService from '../../services/masterDataService';
import { useToast, ToastContainer } from '../common/Toast';

const FixedPriceContract = () => {
  const { openPageHelp } = useHelp();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [contracts, setContracts] = useState([]);
  const [fixedPriceMap, setFixedPriceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formData, setFormData] = useState({
    contractId: '',
    totalValue: '',
    currency: '',
    retentionHoldback: '',
    milestones: [
      { name: '', amount: '', dueDate: '', paymentStatus: 'pending' },
    ],
  });
  const [selectedFixedPrice, setSelectedFixedPrice] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  // Transform backend contract data to frontend format
  const transformContract = (contract, fixedPrice = null) => {
    const milestonesSource =
      fixedPrice?.milestones ||
      contract.milestones ||
      contract.fixedPrice?.milestones ||
      [];
    const normalizedMilestones = Array.isArray(milestonesSource)
      ? milestonesSource.map((milestone) => {
          const paymentStatus = milestone.paymentStatus || milestone.status;
          return {
            name: milestone.name || milestone.title || '',
            date:
              milestone.dueDate || milestone.date || milestone.targetDate || '',
            status:
              paymentStatus === 'paid' || paymentStatus === 'completed'
                ? 'completed'
                : 'pending',
            value: milestone.amount || milestone.value || 0,
            paymentStatus: milestone.paymentStatus || 'pending',
          };
        })
      : [];

    const completedMilestones = normalizedMilestones.filter(
      (m) => m.status === 'completed'
    ).length;
    const progress =
      normalizedMilestones.length > 0
        ? Math.round((completedMilestones / normalizedMilestones.length) * 100)
        : 0;

    // Extract scope and deliverables from contract data
    const scope = contract.scope || contract.fixedPrice?.scope || [];
    const deliverables =
      contract.deliverables || contract.fixedPrice?.deliverables || [];

    return {
      id: contract.contractId || contract.id,
      name: contract.title || contract.name,
      client: contract.counterparty || contract.client,
      status: contract.status?.toLowerCase() || 'active',
      contractStatus: contract.status || '',
      totalValue:
        fixedPrice?.totalValue || contract.amount || contract.totalValue || 0,
      currency: fixedPrice?.currency || contract.currency || 'USD',
      startDate: contract.startDate
        ? new Date(contract.startDate).toISOString().split('T')[0]
        : '',
      endDate: contract.endDate
        ? new Date(contract.endDate).toISOString().split('T')[0]
        : '',
      progress,
      scope: Array.isArray(scope) ? scope : [],
      deliverables: Array.isArray(deliverables) ? deliverables : [],
      milestones: normalizedMilestones,
      changeRequests:
        contract.changeRequests || contract.fixedPrice?.changeRequests || 0,
      qualityScore: contract.qualityScore || 0,
      riskLevel: contract.riskLevel?.toLowerCase() || 'medium',
      fixedPriceId: fixedPrice?._id || fixedPrice?.id || null,
      retentionHoldback: fixedPrice?.retentionHoldback || 0,
      totalPaid: fixedPrice?.totalPaid || 0,
      remainingBalance: fixedPrice?.remainingBalance || 0,
      isReadOnly: String(contract.status || '').toLowerCase() === 'terminated',
    };
  };

  const loadFixedPriceDetails = async (contractsList) => {
    const results = await Promise.all(
      contractsList.map(async (contract) => {
        const identifier = contract.contractId || contract.id;
        if (!identifier) return null;
        try {
          const response = await ContractService.getFixedPrice(identifier);
          const fixedPrice = response?.data || response;
          return fixedPrice ? { contractId: identifier, fixedPrice } : null;
        } catch (error) {
          if (error?.response?.status !== 404) {
            console.error('Error loading fixed-price details:', error);
          }
          return null;
        }
      })
    );

    const fixedPriceLookup = results.reduce((acc, item) => {
      if (item?.contractId && item?.fixedPrice) {
        acc[item.contractId] = item.fixedPrice;
      }
      return acc;
    }, {});

    setFixedPriceMap(fixedPriceLookup);
    return fixedPriceLookup;
  };

  // Load fixed-price contracts from backend
  const loadContracts = async () => {
    setLoading(true);
    try {
      // First, get all contract types and identify which ones have pricingModel = 'Fixed-Price'
      const contractTypesResponse = await MasterDataService.getByCategory('ContractType');
      const contractTypes = contractTypesResponse?.data || [];
      
      // Extract contract type names that have Fixed-Price pricing model
      const fixedPriceTypeNames = contractTypes
        .map(type => {
          let metadata = {};
          if (type.metadata) {
            if (type.metadata instanceof Map) {
              metadata = Object.fromEntries(type.metadata);
            } else if (typeof type.metadata === 'object') {
              metadata = type.metadata;
            }
          }
          
          const pricingModel = metadata.pricingModel || 'Fixed-Price';
          // Return the label (name) if it's a Fixed-Price type
          if (pricingModel === 'Fixed-Price' && (type.label || type.code)) {
            return type.label || type.code;
          }
          return null;
        })
        .filter(Boolean);
      
      // If no Fixed-Price contract types found, still try to load contracts
      // (in case contracts were created before the pricingModel metadata was added)
      let contractsResponse;
      if (fixedPriceTypeNames.length > 0) {
        // Filter contracts by contract type names that have Fixed-Price pricing model
        contractsResponse = await ContractService.getContracts({
          type: fixedPriceTypeNames, // Pass array of contract type names
          page: pagination.page,
          limit: pagination.pageSize,
          includeInactive: true
        });
      } else {
        // Fallback: load all contracts and filter client-side
        // This handles edge cases where contract types might not have metadata set
        contractsResponse = await ContractService.getContracts({
          page: pagination.page,
          limit: 1000, // Get more to filter client-side
          includeInactive: true
        });
      }

      const contractsData = contractsResponse.data || contractsResponse.contracts || [];
      
      // If we had to load all contracts, filter by checking contract types
      let filteredContracts = contractsData;
      if (fixedPriceTypeNames.length === 0 && contractsData.length > 0) {
        // Create a map of contract type names for quick lookup
        const fixedPriceTypeMap = new Set(
          contractTypes
            .filter(type => {
              let metadata = {};
              if (type.metadata) {
                if (type.metadata instanceof Map) {
                  metadata = Object.fromEntries(type.metadata);
                } else if (typeof type.metadata === 'object') {
                  metadata = type.metadata;
                }
              }
              return (metadata.pricingModel || 'Fixed-Price') === 'Fixed-Price';
            })
            .map(type => (type.label || type.code).toLowerCase())
        );
        
        filteredContracts = contractsData.filter(contract => {
          const contractType = (contract.type || '').toLowerCase();
          return fixedPriceTypeMap.has(contractType);
        });
      }
      
      const fixedPriceLookup = await loadFixedPriceDetails(filteredContracts);
      const transformedContracts = filteredContracts.map(contract => {
        const identifier = contract.contractId || contract.id;
        return transformContract(contract, fixedPriceLookup[identifier]);
      });

      setContracts(transformedContracts);
      setPagination((prev) => ({
        ...prev,
        total: contractsResponse.pagination?.totalItems || transformedContracts.length
      }));
    } catch (error) {
      console.error('Error loading fixed-price contracts:', error);
      showError(
        error.response?.data?.message || 'Failed to load fixed-price contracts'
      );
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  // Load contracts on component mount
  useEffect(() => {
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mock data for Fixed-Price contracts (fallback if no backend data)
  const mockContracts = [
    {
      id: 'FP-001',
      name: 'E-commerce Platform Development',
      client: 'TechCorp Solutions',
      status: 'active',
      totalValue: 150000,
      currency: 'USD',
      startDate: '2024-01-15',
      endDate: '2024-06-15',
      progress: 65,
      scope: [
        'Frontend development with React',
        'Backend API development',
        'Database design and implementation',
        'Payment gateway integration',
        'Admin dashboard development',
      ],
      deliverables: [
        'Responsive web application',
        'Mobile app (iOS/Android)',
        'Admin panel',
        'API documentation',
        'User manual',
      ],
      milestones: [
        {
          name: 'Project Kickoff',
          date: '2024-01-15',
          status: 'completed',
          value: 15000,
        },
        {
          name: 'Design Phase Complete',
          date: '2024-02-15',
          status: 'completed',
          value: 30000,
        },
        {
          name: 'Development Phase 1',
          date: '2024-03-15',
          status: 'completed',
          value: 45000,
        },
        {
          name: 'Development Phase 2',
          date: '2024-04-15',
          status: 'in-progress',
          value: 45000,
        },
        {
          name: 'Testing & QA',
          date: '2024-05-15',
          status: 'pending',
          value: 30000,
        },
        {
          name: 'Final Delivery',
          date: '2024-06-15',
          status: 'pending',
          value: 15000,
        },
      ],
      changeRequests: 2,
      qualityScore: 92,
      riskLevel: 'low',
    },
    {
      id: 'FP-002',
      name: 'Mobile App Development',
      client: 'StartupXYZ',
      status: 'active',
      totalValue: 85000,
      currency: 'USD',
      startDate: '2024-02-01',
      endDate: '2024-05-01',
      progress: 40,
      scope: [
        'iOS app development',
        'Android app development',
        'Backend API development',
        'Push notification system',
        'Analytics integration',
      ],
      deliverables: [
        'iOS app (App Store ready)',
        'Android app (Play Store ready)',
        'Backend API',
        'Admin dashboard',
        'Documentation',
      ],
      milestones: [
        {
          name: 'Project Setup',
          date: '2024-02-01',
          status: 'completed',
          value: 8500,
        },
        {
          name: 'UI/UX Design',
          date: '2024-02-15',
          status: 'completed',
          value: 17000,
        },
        {
          name: 'Backend Development',
          date: '2024-03-01',
          status: 'in-progress',
          value: 25500,
        },
        {
          name: 'iOS Development',
          date: '2024-03-15',
          status: 'pending',
          value: 25500,
        },
        {
          name: 'Android Development',
          date: '2024-04-01',
          status: 'pending',
          value: 25500,
        },
        {
          name: 'Testing & Launch',
          date: '2024-05-01',
          status: 'pending',
          value: 8500,
        },
      ],
      changeRequests: 1,
      qualityScore: 88,
      riskLevel: 'medium',
    },
    {
      id: 'FP-003',
      name: 'Enterprise Software Integration',
      client: 'GlobalCorp Inc',
      status: 'completed',
      totalValue: 250000,
      currency: 'USD',
      startDate: '2023-10-01',
      endDate: '2024-01-31',
      progress: 100,
      scope: [
        'Legacy system integration',
        'Data migration',
        'API development',
        'Security implementation',
        'Performance optimization',
      ],
      deliverables: [
        'Integrated system',
        'Data migration scripts',
        'API documentation',
        'Security audit report',
        'Performance benchmarks',
      ],
      milestones: [
        {
          name: 'Analysis & Planning',
          date: '2023-10-01',
          status: 'completed',
          value: 25000,
        },
        {
          name: 'Integration Phase 1',
          date: '2023-11-01',
          status: 'completed',
          value: 50000,
        },
        {
          name: 'Integration Phase 2',
          date: '2023-12-01',
          status: 'completed',
          value: 75000,
        },
        {
          name: 'Testing & Optimization',
          date: '2024-01-01',
          status: 'completed',
          value: 50000,
        },
        {
          name: 'Final Delivery',
          date: '2024-01-31',
          status: 'completed',
          value: 50000,
        },
      ],
      changeRequests: 0,
      qualityScore: 95,
      riskLevel: 'low',
    },
  ];

  // Use mock data only if no contracts loaded from backend (for development/demo purposes)
  // In production, you may want to remove this fallback
  useEffect(() => {
    if (contracts.length === 0 && !loading) {
      // Fallback to mock data if backend returns no contracts
      // This is useful for development/demo purposes
      // setContracts(mockContracts);
      // setPagination(prev => ({ ...prev, total: mockContracts.length }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleDelete = async (contractId) => {
    setIsDeleting(true);
    try {
      const fixedPriceId = contractToDelete?.fixedPriceId;
      if (!fixedPriceId) {
        showError('No fixed-price details found to delete');
        setIsDeleting(false);
        return;
      }
      const response = await ContractService.deleteFixedPrice(
        contractId,
        fixedPriceId
      );
      if (response.success) {
        success('Fixed-price details deleted successfully');
        await loadContracts();
      } else {
        showError(response.message || 'Failed to delete fixed-price details');
      }
      setShowDeleteModal(false);
      setContractToDelete(null);
    } catch (error) {
      console.error('Error deleting fixed-price details:', error);
      showError(
        error.response?.data?.message || 'Failed to delete fixed-price details'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNew = () => {
    if (contracts.length === 0) {
      showError('Create a fixed-price contract before adding details');
      return;
    }
    setFormMode('create');
    setSelectedFixedPrice(null);
    setFormData({
      contractId: '',
      totalValue: '',
      currency: '',
      retentionHoldback: '',
      milestones: [
        { name: '', amount: '', dueDate: '', paymentStatus: 'pending' },
      ],
    });
    setShowFormModal(true);
  };

  const handleView = (contract) => {
    const fixedPrice = fixedPriceMap[contract.id];
    if (!fixedPrice) {
      showError('Fixed-price details not found for this contract');
      return;
    }
    setFormMode('view');
    setSelectedFixedPrice({ ...fixedPrice, contractId: contract.id });
    setFormData({
      contractId: contract.id,
      totalValue: fixedPrice.totalValue || '',
      currency: fixedPrice.currency || contract.currency || '',
      retentionHoldback: fixedPrice.retentionHoldback || 0,
      milestones:
        Array.isArray(fixedPrice.milestones) && fixedPrice.milestones.length > 0
          ? fixedPrice.milestones.map((milestone) => ({
              name: milestone.name || '',
              amount: milestone.amount || '',
              dueDate: milestone.dueDate
                ? new Date(milestone.dueDate).toISOString().split('T')[0]
                : '',
              paymentStatus: milestone.paymentStatus || 'pending',
            }))
          : [{ name: '', amount: '', dueDate: '', paymentStatus: 'pending' }],
    });
    setShowFormModal(true);
  };

  const handleEdit = (contract) => {
    if (contract.isReadOnly) {
      showError('Fixed-price details are read-only for terminated contracts');
      return;
    }
    const fixedPrice = fixedPriceMap[contract.id];
    if (!fixedPrice) {
      showError('Fixed-price details not found for this contract');
      return;
    }
    setFormMode('edit');
    setSelectedFixedPrice({ ...fixedPrice, contractId: contract.id });
    setFormData({
      contractId: contract.id,
      totalValue: fixedPrice.totalValue || '',
      currency: fixedPrice.currency || contract.currency || '',
      retentionHoldback: fixedPrice.retentionHoldback || 0,
      milestones:
        Array.isArray(fixedPrice.milestones) && fixedPrice.milestones.length > 0
          ? fixedPrice.milestones.map((milestone) => ({
              name: milestone.name || '',
              amount: milestone.amount || '',
              dueDate: milestone.dueDate
                ? new Date(milestone.dueDate).toISOString().split('T')[0]
                : '',
              paymentStatus: milestone.paymentStatus || 'pending',
            }))
          : [{ name: '', amount: '', dueDate: '', paymentStatus: 'pending' }],
    });
    setShowFormModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'contractId' && formMode === 'create') {
        const contract = contracts.find((item) => item.id === value);
        if (contract) {
          if (next.totalValue === '') {
            next.totalValue = contract.totalValue || '';
          }
          if (!next.currency) {
            next.currency = contract.currency || '';
          }
        }
      }
      return next;
    });
  };

  const handleMilestoneChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.milestones];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, milestones: updated };
    });
  };

  const handleAddMilestone = () => {
    setFormData((prev) => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        { name: '', amount: '', dueDate: '', paymentStatus: 'pending' },
      ],
    }));
  };

  const handleRemoveMilestone = (index) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.filter((_, idx) => idx !== index),
    }));
  };

  const validateForm = () => {
    if (!formData.contractId) {
      showError('Contract is required');
      return false;
    }
    if (formData.totalValue === '' || Number(formData.totalValue) < 0) {
      showError('Total contract value is required');
      return false;
    }
    if (!formData.currency) {
      showError('Currency is required');
      return false;
    }
    if (
      !Array.isArray(formData.milestones) ||
      formData.milestones.length === 0
    ) {
      showError('At least one milestone is required');
      return false;
    }
    const invalidMilestone = formData.milestones.find(
      (milestone) =>
        !milestone.name || milestone.amount === '' || milestone.dueDate === ''
    );
    if (invalidMilestone) {
      showError('Each milestone requires a name, amount, and due date');
      return false;
    }
    const totalMilestones = formData.milestones.reduce(
      (sum, milestone) => sum + Number(milestone.amount || 0),
      0
    );
    if (totalMilestones > Number(formData.totalValue)) {
      showError('Milestone totals cannot exceed fixed contract value');
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (formMode === 'view') {
      return;
    }
    if (!formData.contractId || !formData.contractId.trim()) {
      showError('Please select a base contract first. Create a contract from Contract Management if needed.');
      return;
    }
    const contract = contracts.find((item) => item.id === formData.contractId);
    if (!contract) {
      showError('Selected contract not found. Please choose a valid contract.');
      return;
    }
    if (contract?.isReadOnly) {
      showError('Fixed-price details are read-only for terminated contracts');
      return;
    }
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        totalValue: Number(formData.totalValue),
        currency: formData.currency,
        retentionHoldback:
          formData.retentionHoldback === ''
            ? 0
            : Number(formData.retentionHoldback),
        milestones: formData.milestones.map((milestone) => ({
          name: milestone.name,
          amount: Number(milestone.amount),
          dueDate: milestone.dueDate,
          paymentStatus: milestone.paymentStatus,
        })),
      };

      if (formMode === 'edit' && selectedFixedPrice?._id) {
        await ContractService.updateFixedPrice(
          formData.contractId,
          selectedFixedPrice._id,
          payload
        );
        success('Fixed-price details updated successfully');
      } else if (formMode === 'create') {
        await ContractService.createFixedPrice(formData.contractId, payload);
        success('Fixed-price details created successfully');
      }

      setShowFormModal(false);
      setSelectedFixedPrice(null);
      await loadContracts();
    } catch (error) {
      console.error('Error saving fixed-price details:', error);
      showError(
        error.response?.data?.message || 'Failed to save fixed-price details'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { class: 'bg-success', text: 'Active' },
      completed: { class: 'bg-primary', text: 'Completed' },
      'on-hold': { class: 'bg-warning', text: 'On Hold' },
      cancelled: { class: 'bg-danger', text: 'Cancelled' },
      terminated: { class: 'bg-danger', text: 'Terminated' },
    };

    const config = statusConfig[status] || {
      class: 'bg-secondary',
      text: status,
    };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getRiskBadge = (riskLevel) => {
    const riskConfig = {
      low: { class: 'bg-success', text: 'Low Risk' },
      medium: { class: 'bg-warning', text: 'Medium Risk' },
      high: { class: 'bg-danger', text: 'High Risk' },
    };

    const config = riskConfig[riskLevel] || {
      class: 'bg-secondary',
      text: riskLevel,
    };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getMilestoneStatus = (status) => {
    const statusConfig = {
      completed: { class: 'text-success', icon: <CheckCircle size={16} /> },
      'in-progress': { class: 'text-warning', icon: <Clock size={16} /> },
      pending: { class: 'text-muted', icon: <Calendar size={16} /> },
    };

    const config = statusConfig[status] || {
      class: 'text-muted',
      icon: <Calendar size={16} />,
    };
    return (
      <span className={`d-flex align-items-center gap-1 ${config.class}`}>
        {config.icon} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const renderOverview = () => (
    <div className="page-stats-grid grid-modern-4 gap-6 mb-8">
      {/* Total Contracts */}
      <div className="relative border border-[var(--border-color)] bg-white">
        <div className="h-1 w-full bg-[var(--primary-color)]" />
        <div className="relative flex items-center gap-3 p-4">
          {/* Icon */}
          <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
            <FileText size={20} style={{ color: '#2D7DD2' }} />
          </div>

          {/* Text */}
          <div>
            <div className="text-[20px] font-bold text-[var(--text-primary)]">
              {contracts.length}
            </div>
            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
              Total Contracts
            </div>
          </div>
        </div>
      </div>

      {/* Active Contracts */}
      <div className="relative border border-[var(--border-color)] bg-white">
        <div className="h-1 w-full bg-[var(--primary-color)]" />
        <div className="relative flex items-center gap-3 p-4">
          {/* Icon */}
          <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
            <CheckCircle size={20} style={{ color: '#2D7DD2' }} />
          </div>

          {/* Text */}
          <div>
            <div className="text-[20px] font-bold text-[var(--text-primary)]">
              {contracts.filter((c) => c.status === 'active').length}
            </div>
            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
              Active Contracts
            </div>
          </div>
        </div>
      </div>

      {/* Total Value */}
      <div className="relative border border-[var(--border-color)] bg-white">
        <div className="h-1 w-full bg-[var(--primary-color)]" />
        <div className="relative flex items-center gap-3 p-4">
          {/* Icon */}
          <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
            <DollarSign size={20} style={{ color: '#2D7DD2' }} />
          </div>

          {/* Text */}
          <div>
            <div className="text-[20px] font-bold text-[var(--text-primary)]">
              $
              {contracts
                .reduce((sum, c) => sum + c.totalValue, 0)
                .toLocaleString()}
            </div>
            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
              Total Value
            </div>
          </div>
        </div>
      </div>

      {/* Change Requests */}
      <div className="relative border border-[var(--border-color)] bg-white">
        <div className="h-1 w-full bg-[var(--primary-color)]" />
        <div className="relative flex items-center gap-3 p-4">
          {/* Icon */}
          <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
            <AlertTriangle size={20} style={{ color: '#2D7DD2' }} />
          </div>

          {/* Text */}
          <div>
            <div className="text-[20px] font-bold text-[var(--text-primary)]">
              {contracts.reduce((sum, c) => sum + c.changeRequests, 0)}
            </div>
            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
              Change Requests
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContracts = () => (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Fixed-Price Contracts</h5>
          </div>
          <div className="card-body" style={{ overflowX: 'auto' }}>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Contract</th>
                    <th>Client</th>
                    <th>Status</th>
                    <th>Value</th>
                    <th>Progress</th>
                    <th>Milestones</th>
                    <th>Quality Score</th>
                    <th>Risk Level</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((contract) => (
                    <tr key={contract.id}>
                      <td>
                        <div>
                          <div className="fw-bold">{contract.name}</div>
                          <small className="text-muted">{contract.id}</small>
                        </div>
                      </td>
                      <td>
                        <div
                          className="text-truncate"
                          style={{ maxWidth: '150px' }}
                        >
                          {contract.client}
                        </div>
                      </td>
                      <td>{getStatusBadge(contract.status)}</td>
                      <td>
                        <div className="fw-bold">
                          ${contract.totalValue.toLocaleString()}
                        </div>
                        <small className="text-muted">
                          {contract.currency}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="progress"
                            style={{ width: '100px', height: '8px' }}
                          >
                            <div
                              className="progress-bar"
                              style={{ width: `${contract.progress}%` }}
                            ></div>
                          </div>
                          <span className="fw-bold">{contract.progress}%</span>
                        </div>
                      </td>
                      <td>
                        <div
                          className="text-truncate"
                          style={{ maxWidth: '200px' }}
                        >
                          {
                            contract.milestones.filter(
                              (m) => m.status === 'completed'
                            ).length
                          }{' '}
                          / {contract.milestones.length}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <Star className="text-warning" size={16} />
                          <span className="fw-bold">
                            {contract.qualityScore}%
                          </span>
                        </div>
                      </td>
                      <td>{getRiskBadge(contract.riskLevel)}</td>
                      <td>
                        <div className="btn-group" role="group">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleView(contract)}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleEdit(contract)}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                              if (contract.isReadOnly) {
                                showError(
                                  'Fixed-price details are read-only for terminated contracts'
                                );
                                return;
                              }
                              if (!contract.fixedPriceId) {
                                showError(
                                  'No fixed-price details found to delete'
                                );
                                return;
                              }
                              setContractToDelete(contract);
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMilestones = () => (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Milestone Tracking</h5>
          </div>
          <div className="card-body" style={{ padding: 'var(--spacing-lg)' }}>
            {contracts.map((contract) => (
              <div key={contract.id} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">{contract.name}</h6>
                  <span className="badge bg-primary">{contract.id}</span>
                </div>

                <div className="milestone-timeline">
                  {contract.milestones.map((milestone, index) => (
                    <div key={index} className="milestone-phase">
                      <div className="milestone-phase-content">
                        <div className="milestone-phase-header">
                          <div className="milestone-phase-title">
                            <h6 className="mb-1">{milestone.name}</h6>
                            <div className="milestone-phase-meta">
                              <div className="milestone-phase-date">
                                <Calendar size={14} className="me-1" />
                                {new Date(milestone.date).toLocaleDateString()}
                              </div>
                              <div className="milestone-phase-value">
                                ${milestone.value.toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="milestone-phase-status">
                            {getMilestoneStatus(milestone.status)}
                          </div>
                        </div>
                        <div className="milestone-phase-progress">
                          <div
                            className={`milestone-phase-indicator ${milestone.status}`}
                          >
                            <div className="milestone-phase-dot"></div>
                            {index < contract.milestones.length - 1 && (
                              <div className="milestone-phase-line"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderScopeDeliverables = () => (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Scope & Deliverables</h5>
          </div>
          <div className="card-body" style={{ padding: 'var(--spacing-lg)' }}>
            {contracts.map((contract) => (
              <div key={contract.id} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="mb-0">{contract.name}</h6>
                  <span className="badge bg-primary">{contract.id}</span>
                </div>

                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="scope-deliverable-card h-100">
                      <div className="scope-deliverable-header">
                        <div className="scope-deliverable-icon">
                          <Target size={20} />
                        </div>
                        <h6 className="scope-deliverable-title">Scope Items</h6>
                      </div>
                      <div className="scope-deliverable-content">
                        <div className="scope-deliverable-list">
                          {contract.scope.map((item, index) => (
                            <div key={index} className="scope-deliverable-item">
                              <div className="scope-deliverable-item-icon">
                                <CheckCircle size={16} />
                              </div>
                              <div className="scope-deliverable-item-text">
                                {item}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="scope-deliverable-card h-100">
                      <div className="scope-deliverable-header">
                        <div className="scope-deliverable-icon">
                          <FileText size={20} />
                        </div>
                        <h6 className="scope-deliverable-title">
                          Deliverables
                        </h6>
                      </div>
                      <div className="scope-deliverable-content">
                        <div className="scope-deliverable-list">
                          {contract.deliverables.map((item, index) => (
                            <div key={index} className="scope-deliverable-item">
                              <div className="scope-deliverable-item-icon">
                                <FileText size={16} />
                              </div>
                              <div className="scope-deliverable-item-text">
                                {item}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
    { id: 'contracts', label: 'Contracts', icon: <FileText size={18} /> },
    { id: 'milestones', label: 'Milestones', icon: <Target size={18} /> },
    { id: 'scope', label: 'Scope & Deliverables', icon: <List size={18} /> },
  ];

  const handleRefresh = () => {
    loadContracts();
    success('Data refreshed successfully');
  };

  const selectedContract = contracts.find(
    (contract) => contract.id === formData.contractId
  );
  const isReadOnlyContract = selectedContract?.isReadOnly;
  const isActiveContract =
    selectedContract?.contractStatus?.toLowerCase() === 'active';
  const formTitle =
    formMode === 'edit'
      ? 'Edit Fixed-Price Details'
      : formMode === 'view'
      ? 'Fixed-Price Details'
      : 'New Fixed-Price Details';

  if (loading && contracts.length === 0) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader
          title="Fixed-Price Contract Management"
          description="Manage fixed-price contracts with scope, deliverables, and milestone tracking"
        />
        <div
          className="page-container"
          style={{ width: '100%', maxWidth: '100%' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid var(--border-color)',
                borderTop: '4px solid #4678be',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            ></div>
            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: '16px',
                fontFamily: 'var(--font-family)',
              }}
            >
              Loading fixed-price contracts...
            </p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Fixed-Price Contract Management"
        description="Manage fixed-price contracts with scope, deliverables, and milestone tracking"
        actions={[
          {
            label: '',
            icon: <RefreshCw size={18} />,
            onClick: handleRefresh,
            variant: 'outline',
            tooltip: 'Refresh data',
            disabled: loading,
          },
          {
            label: '',
            icon: <HelpCircle size={18} />,
            onClick: () => openPageHelp('fixed-price-contracts'),
            variant: 'outline',
            tooltip: 'Help - Fixed-Price Contract Management',
          },
          {
            label: 'New Fixed-Price Details',
            icon: <Plus size={18} />,
            onClick: handleAddNew,
            variant: 'primary',
            tooltip: 'Create fixed-price details',
          },
        ]}
      />
      <div
        className="page-container"
        style={{ width: '100%', maxWidth: '100%' }}
      >
        {/* Tab Navigation */}
        <div className="project-tabs" style={{ marginTop: '32px' }}>
          <div className="project-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${
                  activeTab === tab.id ? 'active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">{tab.icon}</div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="project-tab-content" style={{ marginTop: '32px' }}>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'contracts' && renderContracts()}
          {activeTab === 'milestones' && renderMilestones()}
          {activeTab === 'scope' && renderScopeDeliverables()}
        </div>
      </div>

      {showFormModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{formTitle}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFormModal(false)}
                ></button>
              </div>
              <form onSubmit={handleFormSubmit}>
                <div className="modal-body">
                  {isReadOnlyContract && formMode !== 'view' && (
                    <div className="alert alert-danger">
                      Fixed-price details are read-only for terminated
                      contracts.
                    </div>
                  )}
                  {isActiveContract && formMode === 'edit' && (
                    <div className="alert alert-warning">
                      Fixed contract value cannot be changed after activation.
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Contract</label>
                    <select
                      className="form-select"
                      value={formData.contractId}
                      onChange={(event) =>
                        handleFormChange('contractId', event.target.value)
                      }
                      disabled={formMode !== 'create'}
                    >
                      <option value="">Select a contract</option>
                      {contracts.map((contract) => {
                        const hasFixedPrice = Boolean(
                          fixedPriceMap[contract.id]
                        );
                        return (
                          <option
                            key={contract.id}
                            value={contract.id}
                            disabled={formMode === 'create' && hasFixedPrice}
                          >
                            {contract.name} ({contract.id})
                            {hasFixedPrice ? ' - Details exist' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Total Contract Value</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.totalValue}
                        onChange={(event) =>
                          handleFormChange('totalValue', event.target.value)
                        }
                        disabled={
                          formMode === 'view' ||
                          (formMode === 'edit' && isActiveContract)
                        }
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Currency</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.currency}
                        onChange={(event) =>
                          handleFormChange('currency', event.target.value)
                        }
                        disabled={formMode === 'view'}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Retention / Holdback</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.retentionHoldback}
                        onChange={(event) =>
                          handleFormChange(
                            'retentionHoldback',
                            event.target.value
                          )
                        }
                        disabled={formMode === 'view'}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Milestones</h6>
                    {formMode !== 'view' && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAddMilestone}
                      >
                        Add Milestone
                      </button>
                    )}
                  </div>

                  {formData.milestones.map((milestone, index) => (
                    <div key={index} className="border rounded p-3 mb-3">
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={milestone.name}
                            onChange={(event) =>
                              handleMilestoneChange(
                                index,
                                'name',
                                event.target.value
                              )
                            }
                            disabled={formMode === 'view'}
                          />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            value={milestone.amount}
                            onChange={(event) =>
                              handleMilestoneChange(
                                index,
                                'amount',
                                event.target.value
                              )
                            }
                            disabled={formMode === 'view'}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Due Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={milestone.dueDate}
                            onChange={(event) =>
                              handleMilestoneChange(
                                index,
                                'dueDate',
                                event.target.value
                              )
                            }
                            disabled={formMode === 'view'}
                          />
                        </div>
                        <div className="col-md-2 mb-3">
                          <label className="form-label">Payment Status</label>
                          <select
                            className="form-select"
                            value={milestone.paymentStatus}
                            onChange={(event) =>
                              handleMilestoneChange(
                                index,
                                'paymentStatus',
                                event.target.value
                              )
                            }
                            disabled={formMode === 'view'}
                          >
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                          </select>
                        </div>
                      </div>
                      {formMode !== 'view' && (
                        <div className="text-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveMilestone(index)}
                            disabled={formData.milestones.length === 1}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowFormModal(false)}
                  >
                    Close
                  </button>
                  {formMode !== 'view' && (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSaving || isReadOnlyContract}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete fixed-price details for{' '}
                  <strong>{contractToDelete?.name}</strong>?
                </p>
                <p className="text-muted">
                  The parent contract will remain unchanged.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDelete(contractToDelete?.id)}
                  disabled={isDeleting || !contractToDelete}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Help Modal */}
      <PageHelp />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default FixedPriceContract;